// AdminPage.tsx
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useLocation } from 'wouter';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Clock3,
  Image as ImageIcon,
  Loader2,
  MessageSquare,
  MessageCircle,
  ShieldCheck,
  Star,
  Trash2,
  Users,
  CheckCircle2,
  Gauge,
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';

import UsageAnalytics from '@/components/admin/UsageAnalytics';
import AdminCouponsTab from '@/components/admin/AdminCouponsTab';
import DemographicsCard from '@/components/admin/DemographicsCard';
import AdminGroupEmailTab from '@/components/admin/AdminGroupEmailTab';
import { AdminOverview, API_BASE } from '@/lib/api';

const fetchAdminOverview = async (token: string): Promise<AdminOverview> => {
  const response = await fetch(`${API_BASE}/admin-analytics`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (response.status === 403) throw new Error('not-admin');
  if (response.status === 401) throw new Error('unauthorized');
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.message || 'Failed to fetch admin overview');
  }
  return response.json();
};

const deleteAccount = async ({ identifier, token }: { identifier: { userId?: string; email?: string }; token: string }) => {
  const response = await fetch(`${API_BASE}/admin-analytics`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(identifier),
  });

  const payload = await response.json().catch(() => ({}));
  if (response.status === 403) throw new Error('not-admin');
  if (!response.ok) throw new Error(payload.message || 'Failed to delete account');
  return payload;
};

const formatHour = (hour: number) => {
  const date = new Date();
  date.setHours(hour, 0, 0, 0);
  return date.toLocaleTimeString([], { hour: 'numeric', hour12: true });
};

const colorPalette = ['#22d3ee', '#6366f1', '#f97316', '#10b981', '#f59e0b', '#a855f7'];

export default function AdminPage() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { getToken } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [feedbackTab, setFeedbackTab] = useState<'sessions' | 'general'>('sessions');

  const overviewQuery = useQuery({
    queryKey: ['admin-overview'],
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('unauthorized');
      return fetchAdminOverview(token);
    },
    retry: false,
  });

  const deleteMutation = useMutation({
    mutationFn: async (vars: { userId?: string; email?: string }) => {
      const token = await getToken();
      if (!token) throw new Error('unauthorized');
      return deleteAccount({ identifier: vars, token });
    },
    onSuccess: (payload) => {
      toast({
        title: 'Account deleted',
        description: `User ${payload.removedUserId ?? ''} was removed.`,
      });
      setIdentifier('');
      queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
    },
    onError: (error: Error) => {
      if (error.message === 'not-admin') {
        toast({
          title: 'Access denied',
          description: 'Admin privileges required.',
          variant: 'destructive',
        });
        setLocation('/dashboard');
        return;
      }
      toast({
        title: 'Failed to delete account',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const analytics = overviewQuery.data;

  const peakUsage = useMemo(() => {
    if (!analytics) return [];
    const topHours = [...analytics.usage.peakHours]
      .sort((a, b) => b.sessions - a.sessions)
      .slice(0, 5);
    return topHours.map((row, index) => ({
      ...row,
      color: colorPalette[index % colorPalette.length],
    }));
  }, [analytics]);

  if (overviewQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-white" />
          <p className="mt-3 text-sm text-slate-300">Loading admin analytics…</p>
        </div>
      </div>
    );
  }

  if (overviewQuery.isError || !analytics) {
    const message = (overviewQuery.error as Error)?.message;
    if (message === 'not-admin') {
      return (
        <div className="flex min-h-screen items-center justify-center bg-slate-950">
          <Card className="w-full max-w-md border-slate-700 bg-slate-900">
            <CardHeader className="space-y-2 text-center">
              <AlertTriangle className="mx-auto h-10 w-10 text-amber-400" />
              <CardTitle className="text-2xl text-white">Admin access required</CardTitle>
              <CardDescription className="text-slate-300">
                You need the admin role to view analytics.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button className="w-full bg-white text-slate-900 hover:bg-slate-100" onClick={() => setLocation('/dashboard')}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Return to dashboard
              </Button>
              <Button variant="outline" className="w-full border-slate-600 text-white hover:bg-slate-800" onClick={() => overviewQuery.refetch()}>
                <Loader2 className="mr-2 h-4 w-4" /> Retry with admin session
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <Card className="w-full max-w-md border-slate-700 bg-slate-900">
          <CardHeader className="space-y-2 text-center">
            <AlertTriangle className="mx-auto h-10 w-10 text-red-400" />
            <CardTitle className="text-2xl text-white">Unable to load analytics</CardTitle>
            <CardDescription className="text-slate-300">
              {(overviewQuery.error as Error)?.message ?? 'Please try again later.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button className="w-full bg-white text-slate-900 hover:bg-slate-100" onClick={() => overviewQuery.refetch()}>
              <Loader2 className="mr-2 h-4 w-4" /> Retry fetch
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="flex flex-col gap-4 pb-6 sm:pb-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 px-3 py-1">
                Admin Console
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                className="!text-white hover:bg-slate-800"
                onClick={() => setLocation('/dashboard')}
              >
                <ArrowLeft className="mr-1 h-4 w-4" /> Back
              </Button>
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              Product Health & Audience Pulse
            </h1>
            <p className="max-w-2xl text-sm text-slate-300">
              High-fidelity telemetry and sentiment tracking. Privacy-first architecture ensures user trust remains absolute.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              className="border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
              onClick={() => overviewQuery.refetch()}
              disabled={overviewQuery.isFetching}
            >
              {overviewQuery.isFetching ? (
                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              ) : (
                <Activity className="mr-2 h-3 w-3 text-cyan-400" />
              )}
              Refresh Data
            </Button>
            <div className="flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/60 px-4 py-2 text-xs font-medium text-white">
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
              Anonymous aggregates · No PII stored
            </div>
          </div>
        </div>

        <Tabs defaultValue="dashboard" className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-[600px] mb-8 bg-slate-900 border border-slate-800">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white !text-slate-400">Dashboard</TabsTrigger>
            <TabsTrigger value="email" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white !text-slate-400">Group Email</TabsTrigger>
            {(import.meta as any).env?.VITE_FEATURE_COUPONS === 'true' && (
              <TabsTrigger value="coupons" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white !text-slate-400">Coupons</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid gap-4 sm:gap-6 lg:grid-cols-3">
              {/* Active Footprint */}
              <Card className="border-slate-700 bg-slate-900">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                  <div>
                    <CardTitle className="text-lg text-white">Active footprint</CardTitle>
                    <CardDescription className="text-slate-400 text-xs">Rolling 30 day engagement</CardDescription>
                  </div>
                  <Users className="h-5 w-5 text-sky-400" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-3xl font-semibold text-white">{analytics.userStats.totalUsers.toLocaleString()}</p>
                      <p className="text-xs text-slate-400">Total registered users</p>
                    </div>
                    <Separator className="bg-slate-700" />
                    <div className="grid grid-cols-2 gap-4">
                      <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-3">
                        <p className="text-xs uppercase text-slate-400">Active users</p>
                        <p className="mt-1 text-lg font-semibold text-white">{analytics.userStats.activeUsers}</p>
                        <p className="text-xs text-slate-400">Last 30 days</p>
                      </div>
                      <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-3">
                        <p className="text-xs uppercase text-slate-400">New users</p>
                        <p className="mt-1 text-lg font-semibold text-white">{analytics.userStats.newUsers}</p>
                        <p className="text-xs text-slate-400">Last 30 days</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Engagement Metrics */}
              <Card className="border-slate-700 bg-slate-900">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                  <div>
                    <CardTitle className="text-lg text-white">Engagement metrics</CardTitle>
                    <CardDescription className="text-slate-400 text-xs">Message volume and interactions</CardDescription>
                  </div>
                  <MessageSquare className="h-5 w-5 text-indigo-400" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-3xl font-semibold text-white">{analytics.usage.totalMessages.toLocaleString()}</p>
                      <p className="text-xs text-slate-400">Total messages sent</p>
                    </div>
                    <Separator className="bg-slate-700" />
                    <div className="grid grid-cols-2 gap-4">
                      <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-3">
                        <p className="text-xs uppercase text-slate-400">Monthly messages</p>
                        <p className="mt-1 text-lg font-semibold text-white">{analytics.usage.messagesThisMonth.toLocaleString()}</p>
                        <p className="text-xs text-slate-400">Current month</p>
                      </div>
                      <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-3">
                        <p className="text-xs uppercase text-slate-400">Avg per session</p>
                        <p className="mt-1 text-lg font-semibold text-white">{analytics.usage.averageMessagesPerSession.toFixed(1)}</p>
                        <p className="text-xs text-slate-400">Messages/session</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Account Management */}
              <Card className="border-slate-700 bg-slate-900">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <ShieldCheck className="h-5 w-5 text-amber-400" />
                    Account Management
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    User lifecycle and compliance tools
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="identifier" className="text-xs font-semibold uppercase tracking-tight text-slate-300">
                      User ID or Email
                    </Label>
                    <div className="relative">
                      <Input
                        id="identifier"
                        value={identifier}
                        onChange={(event) => setIdentifier(event.target.value)}
                        placeholder="e.g. user_... or name@example.com"
                        className="border-slate-700 bg-slate-950 py-6 text-white placeholder:text-slate-500 focus:ring-red-500/20"
                      />
                      {identifier && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 text-slate-400 hover:text-white hover:bg-slate-800"
                          onClick={() => setIdentifier('')}
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="destructive"
                    className="group flex w-full items-center justify-center gap-2 py-6 font-semibold bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-950/20"
                    disabled={!identifier || deleteMutation.isPending}
                    onClick={() => {
                      if (confirm("Are you absolutely sure? This will delete all user data permanently.")) {
                        const trimmed = identifier.trim();
                        const isEmail = trimmed.includes('@');
                        deleteMutation.mutate(isEmail ? { email: trimmed } : { userId: trimmed });
                      }
                    }}
                  >
                    {deleteMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 transition-transform group-hover:scale-110" />
                    )}
                    Delete Account
                  </Button>
                  <div className="rounded-lg bg-amber-500/5 p-3 text-xs leading-relaxed text-amber-300/80 border border-amber-500/10">
                    <AlertTriangle className="mr-1 inline h-3 w-3" />
                    Warning: Account deletion removes all user data permanently.
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Coach Engagement & Peak Hours */}
            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              {/* Coach Engagement */}
              <Card className="border-slate-700 bg-slate-900">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-cyan-400" />
                    Coach Engagement Mix
                  </CardTitle>
                  <CardDescription className="text-slate-400">Distribution of sessions across coach personas</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {analytics.coachEngagement.topCoaches.map((coach, i) => (
                    <div key={i} className="space-y-1">
                      <div className="flex justify-between text-sm text-white">
                        <span className="capitalize text-white">{coach.coachType.replace('-', ' ')}</span>
                        <span className='text-white'>{coach.sessions} sessions ({coach.percentage.toFixed(1)}%)</span>
                      </div>
                      <div className="h-2 w-full bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-cyan-500 transition-all"
                          style={{ width: `${coach.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Peak Usage */}
              <Card className="border-slate-700 bg-slate-900">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Clock3 className="h-5 w-5 text-amber-400" />
                    Peak Activity Hours
                  </CardTitle>
                  <CardDescription className="text-slate-400">High-traffic periods for AI interventions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {peakUsage.map((item, i) => (
                      <div key={i} className="flex items-center gap-4">
                        <div className="text-xs font-medium w-16 text-white">{formatHour(item.hour)}</div>
                        <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${(item.sessions / peakUsage[0].sessions) * 100}%`,
                              backgroundColor: item.color
                            }}
                          />
                        </div>
                        <div className="text-xs font-semibold text-white w-8">{item.sessions}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Demographics */}
            <div className="mt-6">
              <DemographicsCard
                roles={analytics.demographics.roles}
                industries={analytics.demographics.industries}
                experience={analytics.demographics.experience}
              />
            </div>

            {/* Feedback Section */}
            <div className="mt-8">
              <Card className="border-slate-800 bg-slate-900/50 shadow-2xl overflow-hidden">
                <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-b border-slate-800 bg-slate-900/80 p-6 gap-4">
                  <div className="space-y-1">
                    <CardTitle className="text-xl font-bold text-white flex items-center gap-2">
                      <MessageCircle className="h-5 w-5 text-indigo-400" />
                      Voice of the User
                    </CardTitle>
                    <CardDescription className="text-slate-400 font-medium">
                      Sentiment analysis & community feedback loops
                    </CardDescription>
                  </div>

                  {/* Tab Switcher with better visibility */}
                  <div className="flex gap-1 p-1 bg-slate-950 rounded-xl border border-slate-800 shadow-inner">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFeedbackTab('sessions')}
                      className={`h-9 px-4 text-xs font-bold transition-all duration-200 rounded-lg ${feedbackTab === 'sessions'
                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/20'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                        }`}
                    >
                      Session Feedback
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFeedbackTab('general')}
                      className={`h-9 px-4 text-xs font-bold transition-all duration-200 rounded-lg ${feedbackTab === 'general'
                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/20'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                        }`}
                    >
                      General Reports
                    </Button>
                  </div>
                </CardHeader>

                <CardContent className="p-0">
                  <div className="overflow-x-auto overflow-y-hidden">
                    <table className="w-full text-sm border-collapse">
                      <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-950/40 border-b border-slate-800">
                        <tr>
                          <th className="px-6 py-4 text-left font-bold !text-white">User Identity</th>
                          <th className="px-6 py-4 text-left font-bold !text-white">Feedback Detail</th>
                          {feedbackTab === 'general' && (
                            <th className="px-6 py-4 text-center font-bold !text-white">Image</th>
                          )}
                          <th className="px-6 py-4 text-right font-bold !text-white">Timing</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/50 bg-slate-900/20">
                        {feedbackTab === 'sessions' ? (
                          analytics.feedback.sessions.length === 0 ? (
                            <tr><td colSpan={4} className="px-6 py-16 text-center text-slate-500 italic">No session feedback recorded yet</td></tr>
                          ) : (
                            analytics.feedback.sessions.map((item) => (
                              <tr key={item.id} className="group hover:bg-slate-800/40 transition-all duration-200">
                                <td className="px-6 py-5 align-top">
                                  <div className="flex flex-col">
                                    <span className="font-bold text-white group-hover:text-indigo-300 transition-colors uppercase tracking-tight">
                                      {item.userName || 'Anonymous User'}
                                    </span>
                                    <span className="text-xs text-slate-500 font-mono mt-0.5 tracking-tighter">
                                      {item.userEmail || 'no-email-provided'}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-5">
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-3">
                                      <Badge
                                        variant="outline"
                                        className={`px-2 py-0.5 font-black text-[10px] border-2 shadow-sm ${item.npsScore >= 8
                                          ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                                          : item.npsScore >= 5
                                            ? 'border-amber-500/30 bg-amber-500/10 text-amber-400'
                                            : 'border-red-500/30 bg-red-500/10 text-red-400'
                                          }`}
                                      >
                                        NPS: {item.npsScore}
                                      </Badge>
                                      <div className="flex gap-0.5">
                                        {[...Array(5)].map((_, i) => (
                                          <Star key={i} className={`h-3 w-3 ${i < Math.floor(item.npsScore / 2) ? 'text-amber-400 fill-amber-400' : 'text-slate-700'}`} />
                                        ))}
                                      </div>
                                    </div>
                                    <p className="text-slate-200 leading-relaxed max-w-xl italic">
                                      {item.comment || 'The user didn\'t leave a specific comment, but provided a rating.'}
                                    </p>
                                  </div>
                                </td>
                                <td className="px-6 py-5 text-right align-top">
                                  <span className="text-xs font-bold text-slate-500 font-mono bg-slate-950 px-2 py-1 rounded">
                                    {new Date(item.submittedAt).toLocaleDateString()}
                                  </span>
                                </td>
                              </tr>
                            ))
                          )
                        ) : (
                          analytics.feedback.general.length === 0 ? (
                            <tr><td colSpan={4} className="px-6 py-16 text-center text-slate-500 italic">No general bug reports found</td></tr>
                          ) : (
                            analytics.feedback.general.map((item) => (
                              <tr key={item.id} className="group hover:bg-slate-800/40 transition-all duration-200">
                                <td className="px-6 py-5 align-top">
                                  <div className="flex flex-col gap-2">
                                    <span className="font-bold text-white group-hover:text-indigo-300 transition-colors uppercase tracking-tight truncate max-w-[150px]">
                                      {item.email || 'Anonymous'}
                                    </span>
                                    <Badge className="w-fit bg-indigo-500/10 text-indigo-400 border border-indigo-500/30 text-[9px] font-black uppercase tracking-tighter">
                                      {item.category}
                                    </Badge>
                                  </div>
                                </td>
                                <td className="px-6 py-5">
                                  <div className="space-y-3">
                                    <p className="text-slate-200 leading-relaxed font-medium max-w-xl">
                                      {item.description}
                                    </p>
                                    <div className="flex items-center gap-2">
                                      <div className={`h-2 w-2 rounded-full animate-pulse ${item.status === 'resolved' ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]'}`} />
                                      <span className={`text-[10px] font-black uppercase tracking-widest ${item.status === 'resolved' ? 'text-emerald-500' : 'text-amber-500'}`}>
                                        {item.status}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-5 text-center align-top">
                                  {item.screenshotUrl ? (
                                    <a href={item.screenshotUrl} target="_blank" rel="noopener noreferrer" title="View full image">
                                      <img
                                        src={item.screenshotUrl}
                                        alt="Feedback screenshot"
                                        className="inline-block h-12 w-12 rounded-lg object-cover border border-slate-700 hover:border-indigo-500 hover:scale-110 transition-all cursor-pointer shadow-md"
                                      />
                                    </a>
                                  ) : (
                                    <span className="text-slate-600 text-xs">—</span>
                                  )}
                                </td>
                                <td className="px-6 py-5 text-right align-top">
                                  <span className="text-xs font-bold text-slate-500 font-mono bg-slate-950 px-2 py-1 rounded">
                                    {new Date(item.createdAt).toLocaleDateString()}
                                  </span>
                                </td>
                              </tr>
                            ))
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
            {/* Usage Analytics */}
            <div className="mt-6">
              <UsageAnalytics />
            </div>
          </TabsContent>

          <TabsContent value="email" className="mt-0">
            <AdminGroupEmailTab />
          </TabsContent>

          {(import.meta as any).env?.VITE_FEATURE_COUPONS === 'true' && (
            <TabsContent value="coupons" className="mt-0">
              <AdminCouponsTab />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
